/**
 * Cliente fino para os endpoints `/transcription/...` documentados em
 * `transcriptions.md`. Mantemos as chamadas centralizadas para que a página
 * e o painel não precisem replicar o tratamento de cabeçalhos/erros.
 *
 * Convenções:
 *   - Sempre usa `process.env.REACT_APP_API_BASE_URL` via `getApiBaseUrl()`.
 *     A base já inclui (ou não) o prefixo de versão conforme o ambiente; as
 *     URLs construídas aqui NÃO devem repetir `/v1` para evitar `//v1/v1`.
 *   - Token JWT é passado como argumento (vem de `useAuth()`); evitamos
 *     dependência implícita do contexto.
 *   - Em caso de erro, lança um `Error` cuja `.message` é o `detail` do
 *     backend (ou `<status> <statusText>` como fallback).
 *   - Toda função aceita um último argumento opcional `{ signal }` para
 *     permitir cancelamento via `AbortController` quando o componente é
 *     desmontado (evita requests órfãs e mascara erros pós-unmount).
 *   - Erros de aborto (`AbortError`) propagam com `.aborted = true` para
 *     que o consumidor possa ignorá-los facilmente.
 */

import { getApiBaseUrl } from "../config";

const API_BASE_URL = getApiBaseUrl();
const SERVICE = "DOCUMENT_CREATION";

function buildHeaders(token, extra = {}) {
  return {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
    ServiceName: SERVICE,
    ...extra,
  };
}

async function parseError(response) {
  let detail = `${response.status} ${response.statusText || ""}`.trim();
  try {
    const body = await response.json();
    if (body) {
      if (typeof body.detail === "string") detail = body.detail;
      else if (Array.isArray(body.detail)) {
        detail = body.detail
          .map((d) => d?.msg || JSON.stringify(d))
          .join("; ");
      } else if (typeof body.message === "string") {
        detail = body.message;
      }
    }
  } catch (_) {
    /* ignore parse error */
  }
  const err = new Error(detail || "Erro de comunicação com a API");
  err.status = response.status;
  return err;
}

/**
 * Executa o `fetch` e converte `AbortError` em um erro com `.aborted = true`
 * para que os consumidores possam ignorar de forma uniforme requests
 * canceladas (evitando setState após unmount e mensagens espúrias).
 */
async function safeFetch(url, init) {
  try {
    return await fetch(url, init);
  } catch (err) {
    if (err && (err.name === "AbortError" || err.code === 20)) {
      const aborted = new Error("Request abortada");
      aborted.aborted = true;
      aborted.name = "AbortError";
      throw aborted;
    }
    throw err;
  }
}

/**
 * Verifica se um erro veio de um `AbortController`. Útil para que os
 * componentes consumidores possam diferenciar cancelamentos legítimos de
 * falhas reais.
 */
export function isAbortError(err) {
  return Boolean(err && (err.aborted || err.name === "AbortError"));
}

/**
 * GET /transcription/transcription_review/{job_id}
 *
 * @param {string} token
 * @param {number|string} jobId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ job_id:number, task_id:number, review_status:string,
 *                     transcriptions: any[] }>}
 */
export async function listTranscriptions(token, jobId, options = {}) {
  const url = `${API_BASE_URL}/transcription/transcription_review/${encodeURIComponent(jobId)}`;
  const response = await safeFetch(url, {
    method: "GET",
    headers: buildHeaders(token),
    signal: options.signal,
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * GET /transcription/transcription_review/{job_id}/{transcription_id}
 *
 * @param {string} token
 * @param {number|string} jobId
 * @param {string} transcriptionId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 */
export async function getTranscription(token, jobId, transcriptionId, options = {}) {
  const url = `${API_BASE_URL}/transcription/transcription_review/${encodeURIComponent(
    jobId,
  )}/${encodeURIComponent(transcriptionId)}`;
  const response = await safeFetch(url, {
    method: "GET",
    headers: buildHeaders(token),
    signal: options.signal,
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * POST /transcription/transcription_review/{job_id}/{transcription_id}/save
 *
 * Salva o conteúdo revisado SEM aprovar. Se a transcrição já estava aprovada,
 * o backend a reabre (zera approved_by/approval_timestamp).
 */
export async function saveTranscription(
  token,
  jobId,
  transcriptionId,
  transcriptionContent,
  options = {},
) {
  const url = `${API_BASE_URL}/transcription/transcription_review/${encodeURIComponent(
    jobId,
  )}/${encodeURIComponent(transcriptionId)}/save`;
  const response = await safeFetch(url, {
    method: "POST",
    headers: buildHeaders(token, { "Content-Type": "application/json" }),
    body: JSON.stringify({ transcription: transcriptionContent }),
    signal: options.signal,
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * POST /transcription/transcription_review/{job_id}/{transcription_id}/approve
 *
 * @param {object} [transcriptionContent] se omitido, aprova o último `save`
 */
export async function approveTranscription(
  token,
  jobId,
  transcriptionId,
  transcriptionContent,
  options = {},
) {
  const url = `${API_BASE_URL}/transcription/transcription_review/${encodeURIComponent(
    jobId,
  )}/${encodeURIComponent(transcriptionId)}/approve`;
  const init = {
    method: "POST",
    headers: buildHeaders(token, { "Content-Type": "application/json" }),
    signal: options.signal,
  };
  if (transcriptionContent !== undefined) {
    init.body = JSON.stringify({ transcription: transcriptionContent });
  }
  const response = await safeFetch(url, init);
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * POST /transcription/transcription_review/{job_id}/conclude
 *
 * Finaliza a etapa de revisão (todas as transcrições devem estar `approved`).
 */
export async function concludeTranscriptionReview(token, jobId, options = {}) {
  const url = `${API_BASE_URL}/transcription/transcription_review/${encodeURIComponent(
    jobId,
  )}/conclude`;
  const response = await safeFetch(url, {
    method: "POST",
    headers: buildHeaders(token),
    signal: options.signal,
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * POST /transcription/transcription_review/{job_id}/{transcription_id}/reopen
 *
 * "Desaprovar" uma transcrição já aprovada. Operação SIMÉTRICA ao
 * `/approve`: zera `approved_by` / `approval_timestamp` no backend SEM
 * alterar o conteúdo (`transcription`), `last_saved_timestamp` ou
 * `last_saved_by`.
 *
 * Diferente do uso anterior baseado em `/save`, este endpoint NÃO recebe
 * `transcription` no body — a operação é puramente de desbloqueio.
 *
 * NOTA: este endpoint AINDA NÃO EXISTE no backend; especificação
 * detalhada (request, response, erros) em `transcription-frontend-changes.md`.
 * Enquanto não publicar, tentamos a chamada e, em caso de 404 / 405,
 * fazemos fallback transparente para `/save` (que tem o efeito colateral
 * documentado de reabrir a aprovação ao re-salvar). O fallback será
 * removido assim que o endpoint estiver disponível.
 *
 * @returns {Promise<{ message:string, job_id:number, task_id:number,
 *                     transcription_id:string, approved:false }>}
 */
export async function reopenTranscriptionApproval(
  token,
  jobId,
  transcriptionId,
  options = {},
) {
  const url = `${API_BASE_URL}/transcription/transcription_review/${encodeURIComponent(
    jobId,
  )}/${encodeURIComponent(transcriptionId)}/reopen`;
  const response = await safeFetch(url, {
    method: "POST",
    headers: buildHeaders(token),
    signal: options.signal,
  });
  if (response.ok) return response.json();
  if (response.status === 404 || response.status === 405) {
    /* Fallback transparente enquanto o backend não publica o endpoint
       específico. Para usar `/save` precisamos enviar o conteúdo atual,
       que recuperamos via GET — assim o conteúdo permanece inalterado
       pelo "save" técnico (cujo único propósito aqui é zerar a
       aprovação, conforme `/save` documentado em transcriptions.md §3). */
    const detail = await getTranscription(token, jobId, transcriptionId, {
      signal: options.signal,
    });
    const currentContent = detail?.transcription?.transcription;
    return saveTranscription(
      token,
      jobId,
      transcriptionId,
      currentContent,
      options,
    );
  }
  throw await parseError(response);
}

/**
 * Alias para retrocompatibilidade do nome usado pela página
 * (`cancelTranscriptionApproval`). Encaminha para `reopenTranscriptionApproval`
 * — ignora o argumento `transcriptionContent` (mantido por compatibilidade
 * de assinatura antiga; o reopen não recebe conteúdo).
 */
export async function cancelTranscriptionApproval(
  token,
  jobId,
  transcriptionId,
  optionsOrLegacyContent,
  legacyOptions,
) {
  /* Detecta se o consumidor ainda chama com a assinatura antiga
     (transcriptionContent, options). Se o 4º arg parecer "options"
     (tem `signal`), trata como novo formato. */
  const looksLikeOptions =
    optionsOrLegacyContent &&
    typeof optionsOrLegacyContent === "object" &&
    !Array.isArray(optionsOrLegacyContent) &&
    !("speakers" in optionsOrLegacyContent) &&
    !("sentences" in optionsOrLegacyContent);
  const options = looksLikeOptions
    ? optionsOrLegacyContent
    : legacyOptions || {};
  return reopenTranscriptionApproval(token, jobId, transcriptionId, options);
}

/**
 * Extrai o `filename` proposto pelo servidor a partir do header
 * `Content-Disposition`. Suporta as duas formas que o backend envia
 * (RFC 5987 — `filename*=UTF-8''nome.ext` — e ASCII tradicional —
 * `filename="nome.ext"`); a forma RFC 5987 tem prioridade, pois é a
 * única segura para nomes não-ASCII.
 *
 * Devolve `null` quando o header está ausente ou não bate com nenhum
 * dos formatos esperados — nesse caso o consumidor cai para um nome
 * default próprio.
 */
function parseContentDispositionFilename(header) {
  if (!header) return null;
  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch (_) {
      /* nome mal-codificado: cai para o filename ASCII abaixo */
    }
  }
  const asciiMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  if (asciiMatch) return asciiMatch[1].trim();
  return null;
}

/**
 * Helper interno: faz GET autenticado, devolve o `Blob` do corpo e o
 * `filename` extraído do `Content-Disposition`. Centraliza o tratamento
 * de erro (4xx → `parseError`, 5xx → mantém status para que o consumidor
 * possa decidir entre fallback ou propagar) em um único ponto.
 */
async function fetchAuthenticatedBlob(url, token, signal) {
  const response = await safeFetch(url, {
    method: "GET",
    headers: buildHeaders(token),
    signal,
  });
  if (!response.ok) throw await parseError(response);
  const blob = await response.blob();
  const filename = parseContentDispositionFilename(
    response.headers.get("Content-Disposition"),
  );
  return { blob, filename };
}

/**
 * GET /transcription/transcription_review/{job_id}/{transcription_id}/download/pdf
 *
 * Baixa a transcrição como PDF gerado no backend (layout padronizado,
 * com cabeçalho/rodapé, lista de participantes e tabela de frases).
 *
 * @param {string} token
 * @param {number|string} jobId
 * @param {string} transcriptionId
 * @param {object} [options]
 * @param {"approved"|"draft"} [options.version]  default `"approved"`
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ blob: Blob, filename: string|null }>}
 */
export async function downloadTranscriptionPdf(
  token,
  jobId,
  transcriptionId,
  options = {},
) {
  const version = options.version || "approved";
  const url = `${API_BASE_URL}/transcription/transcription_review/${encodeURIComponent(
    jobId,
  )}/${encodeURIComponent(transcriptionId)}/download/pdf?version=${encodeURIComponent(
    version,
  )}`;
  return fetchAuthenticatedBlob(url, token, options.signal);
}

/**
 * GET /transcription/transcription_review/{job_id}/{transcription_id}/download/docx
 *
 * Baixa a transcrição como DOCX (OOXML real, gerado no backend com
 * `python-docx`). Substitui o `.doc` (Word-HTML) que o frontend gera
 * como fallback.
 */
export async function downloadTranscriptionDocx(
  token,
  jobId,
  transcriptionId,
  options = {},
) {
  const version = options.version || "approved";
  const url = `${API_BASE_URL}/transcription/transcription_review/${encodeURIComponent(
    jobId,
  )}/${encodeURIComponent(transcriptionId)}/download/docx?version=${encodeURIComponent(
    version,
  )}`;
  return fetchAuthenticatedBlob(url, token, options.signal);
}

/**
 * GET /transcription/transcription_review/{job_id}/{transcription_id}/download/json
 *
 * Baixa a transcrição como JSON. Estrutura idêntica à gerada pelo
 * fallback local (`{filename, metadata, transcription}`), garantindo
 * que consumidores externos do JSON (scripts/automações) não precisem
 * mudar nada quando o frontend migra para o backend.
 */
export async function downloadTranscriptionJson(
  token,
  jobId,
  transcriptionId,
  options = {},
) {
  const version = options.version || "approved";
  const url = `${API_BASE_URL}/transcription/transcription_review/${encodeURIComponent(
    jobId,
  )}/${encodeURIComponent(transcriptionId)}/download/json?version=${encodeURIComponent(
    version,
  )}`;
  return fetchAuthenticatedBlob(url, token, options.signal);
}

/**
 * Endpoint readonly para consumir a transcrição depois da etapa de revisão
 * (quando o painel de transcrição é aberto como popup ao clicar em um
 * arquivo de mídia já revisado).
 *
 * NOTA: este endpoint AINDA NÃO EXISTE no backend; a implementação é apenas
 * preparatória, com o caminho proposto. Quando o backend disponibilizar a
 * rota correspondente, basta atualizar o path abaixo. Enquanto isso, a
 * função lança um erro claro para que a UI possa apresentar uma mensagem
 * amigável ao usuário ("transcrição indisponível neste estado do job").
 *
 * @param {string} token
 * @param {number|string} jobId
 * @param {string} transcriptionId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ transcription:any, approved_by?:number, approval_timestamp?:string }>}
 */
export async function getReadonlyTranscription(
  token,
  jobId,
  transcriptionId,
  options = {},
) {
  /* TODO(backend): substituir pelo endpoint definitivo quando disponível.
     Caminho proposto: GET /transcription/{job_id}/{transcription_id} */
  const url = `${API_BASE_URL}/transcription/${encodeURIComponent(
    jobId,
  )}/${encodeURIComponent(transcriptionId)}`;
  const response = await safeFetch(url, {
    method: "GET",
    headers: buildHeaders(token),
    signal: options.signal,
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}
